import { useQuery } from '@tanstack/react-query';
import { collection, getCountFromServer, query, where } from 'firebase/firestore';
import { EventTypeSchema, type EventType } from '@casetrace/shared';
import { db } from '../lib/firebase';
import { EVENTS_COLLECTION, typesenseClient } from '../lib/typesense';

type EventTypeCounts = Partial<Record<EventType, number>>;

const COUNTED_EVENT_TYPES: EventType[] = [
  'email',
  'document',
  'sms',
  'imessage',
  'whatsapp',
  'meeting',
  'note',
];

export interface EventFacetCounts {
  typeCounts: EventTypeCounts;
  attachmentCount: number;
}

interface FacetCount {
  value?: unknown;
  count?: unknown;
}

interface FacetResult {
  field_name?: unknown;
  counts?: FacetCount[];
}

interface FacetSearchResponse {
  facet_counts?: FacetResult[];
}

function toFilterValue(value: string): string {
  return `\`${value.replaceAll('`', '\\`')}\``;
}

function hasCounts(counts: EventFacetCounts): boolean {
  return Object.keys(counts.typeCounts).length > 0 || counts.attachmentCount > 0;
}

function parseFacetCounts(raw: unknown): EventFacetCounts {
  const response = raw as FacetSearchResponse;
  const typeFacet = response.facet_counts?.find((facet) => facet.field_name === 'type');
  const attachmentFacet = response.facet_counts?.find(
    (facet) => facet.field_name === 'hasAttachments',
  );
  const typeCounts: EventTypeCounts = {};
  let attachmentCount = 0;

  for (const entry of typeFacet?.counts ?? []) {
    const type = EventTypeSchema.safeParse(entry.value);
    if (!type.success || typeof entry.count !== 'number') continue;
    typeCounts[type.data] = entry.count;
  }

  for (const entry of attachmentFacet?.counts ?? []) {
    if ((entry.value === true || entry.value === 'true') && typeof entry.count === 'number') {
      attachmentCount = entry.count;
    }
  }

  return { typeCounts, attachmentCount };
}

async function fetchTypesenseCounts(caseId: string): Promise<EventFacetCounts> {
  const raw = await typesenseClient
    ?.collections(EVENTS_COLLECTION)
    .documents()
    .search({
      q: '*',
      query_by: 'title,contentPreview',
      filter_by: `caseId:=${toFilterValue(caseId)}`,
      facet_by: 'type,hasAttachments',
      per_page: 0,
    });

  return parseFacetCounts(raw);
}

async function fetchFirestoreTypeCounts(caseId: string): Promise<EventFacetCounts> {
  const eventsRef = collection(db, 'cases', caseId, 'events');
  const [entries, attachmentsSnap] = await Promise.all([
    Promise.all(
      COUNTED_EVENT_TYPES.map(async (type) => {
        const snap = await getCountFromServer(query(eventsRef, where('type', '==', type)));
        return [type, snap.data().count] as const;
      }),
    ),
    getCountFromServer(query(eventsRef, where('metadata.hasAttachments', '==', true))),
  ]);
  const typeCounts = Object.fromEntries(entries.filter(([, count]) => count > 0));

  return {
    typeCounts,
    attachmentCount: attachmentsSnap.data().count,
  };
}

async function fetchFirestoreCountsForTypes(
  caseId: string,
  types: readonly EventType[],
): Promise<EventTypeCounts> {
  if (types.length === 0) return {};
  const eventsRef = collection(db, 'cases', caseId, 'events');
  const entries = await Promise.all(
    types.map(async (type) => {
      const snap = await getCountFromServer(query(eventsRef, where('type', '==', type)));
      return [type, snap.data().count] as const;
    }),
  );
  return Object.fromEntries(entries.filter(([, count]) => count > 0));
}

async function fetchEventTypeCounts(caseId: string): Promise<EventFacetCounts> {
  if (!typesenseClient) return fetchFirestoreTypeCounts(caseId);

  try {
    const counts = await fetchTypesenseCounts(caseId);
    if (!hasCounts(counts)) return fetchFirestoreTypeCounts(caseId);

    const missingCountedTypes = COUNTED_EVENT_TYPES.filter(
      (type) => counts.typeCounts[type] === undefined,
    );
    if (missingCountedTypes.length === 0) {
      return counts;
    }

    const firestoreBackfill = await fetchFirestoreCountsForTypes(caseId, missingCountedTypes);
    return {
      ...counts,
      typeCounts: {
        ...counts.typeCounts,
        ...firestoreBackfill,
      },
    };
  } catch (error) {
    console.warn(
      'Kunne ikke hente hendelsestype-tellinger fra Typesense, bruker Firestore fallback.',
      error,
    );
    return fetchFirestoreTypeCounts(caseId);
  }
}

export function useEventTypeCounts(caseId: string) {
  return useQuery({
    queryKey: ['event-type-counts', caseId] as const,
    queryFn: () => fetchEventTypeCounts(caseId),
    enabled: Boolean(caseId),
    staleTime: 30_000,
  });
}
